import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  Package,
  Sparkles,
  Settings as SettingsIcon,
  BarChart3,
  Users,
  LogOut,
  ArrowLeftRight,
  Store,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";

const LINKS = [
  { to: "/manage", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/manage/orders", label: "Orders", icon: ClipboardList },
  { to: "/manage/products", label: "Products", icon: Package },
  { to: "/manage/specials", label: "Specials", icon: Sparkles },
  { to: "/manage/reports", label: "Reports", icon: BarChart3 },
  { to: "/manage/accounts", label: "Accounts", icon: Users },
  { to: "/manage/settings", label: "Settings", icon: SettingsIcon },
];

export function AdminLayout({ children, businessId, onBusinessChange }) {
  const { signOut } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const theme = getTheme(businessId);
  const other = businessId === "baking_jays" ? "rubys" : "baking_jays";

  const isActive = (l) => (l.end ? loc.pathname === l.to : loc.pathname.startsWith(l.to));

  const SidebarInner = (
    <>
      <div className="p-5">
        <Link to="/manage" className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full flex items-center justify-center font-display font-bold" style={{ background: "var(--accent)", color: "#1a1400" }}>
            {theme.name.charAt(0)}
          </div>
          <div className="leading-tight">
            <div className="font-display text-base" style={{ color: "var(--hero-fg)" }}>{theme.name}</div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "color-mix(in srgb, var(--hero-fg) 60%, transparent)" }}>Admin</div>
          </div>
        </Link>
      </div>

      <button
        onClick={() => { onBusinessChange(other); setOpen(false); }}
        data-testid="admin-switch-business"
        className="mx-4 mb-4 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition"
        style={{ background: "color-mix(in srgb, var(--hero-fg) 12%, transparent)", color: "var(--hero-fg)" }}
      >
        <ArrowLeftRight size={14} /> Manage {getTheme(other).name}
      </button>

      <nav className="px-3 space-y-1 flex-1">
        {LINKS.map((l) => {
          const Icon = l.icon;
          const active = isActive(l);
          return (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${l.label.toLowerCase()}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition"
              style={active
                ? { background: "var(--hero-fg)", color: "var(--bg)" }
                : { color: "color-mix(in srgb, var(--hero-fg) 78%, transparent)" }}
            >
              <Icon size={18} /> {l.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 space-y-1">
        <Link to="/shop" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold" style={{ color: "color-mix(in srgb, var(--hero-fg) 78%, transparent)" }}>
          <Store size={18} /> View storefront
        </Link>
        <button onClick={() => { signOut(); nav("/shop"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold" style={{ color: "color-mix(in srgb, var(--hero-fg) 78%, transparent)" }}>
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col hero-gradient sticky top-0 h-screen">{SidebarInner}</aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 flex flex-col hero-gradient">{SidebarInner}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--border)" }}>
          <button onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu /></button>
          <span className="font-display text-lg" style={{ color: "var(--ink)" }}>{theme.name} Admin</span>
          <Link to="/shop"><Store size={20} /></Link>
        </div>
        <div className="p-5 sm:p-8 max-w-6xl">{children}</div>
      </div>
    </div>
  );
}
